import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { CheckCircle2, XCircle, Loader2, Clock, RefreshCcw } from 'lucide-react';

// Landing page after returning from the Pine Labs hosted UPI checkout.
// Confirms the payment server-side and shows the result.
export default function PaymentReturnPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const pid = params.get('pid');
    const [state, setState] = useState('loading'); // loading | completed | pending | failed
    const [message, setMessage] = useState('Confirming your payment…');
    const ran = useRef(false);
    const [reason, setReason] = useState('');
    const snap = (() => { try { return JSON.parse(sessionStorage.getItem('ee_retry') || 'null'); } catch (_) { return null; } })();
    const retryRazorpay = () => {
        sessionStorage.setItem('ee_retry', JSON.stringify({ ...snap, reason: reason ? `Pine Labs status: ${reason}` : 'Pine Labs payment was not completed' }));
        const q = new URLSearchParams({ platform: snap.platform || 'olaparty', retry: 'razorpay' });
        if (snap.kind === 'plan' && snap.plan_id) q.set('plan', snap.plan_id);
        navigate(`/recharge?${q.toString()}`);
    };

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        if (!pid) { setState('failed'); setMessage('Missing payment reference.'); return; }
        api.post('/recharge/pinelabs/verify', { payment_id: pid })
            .then(({ data }) => {
                const s = data.payment_status;
                if (s !== 'failed' && s !== 'created') sessionStorage.removeItem('ee_retry');
                if (s === 'completed' && data.kind === 'promotion') { setState('completed'); setMessage('Payment received! Your host banner is awaiting admin approval — track it on the Promote a Host page.'); }
                else if (s === 'completed') { setState('completed'); setMessage('Your recharge has been delivered successfully.'); }
                else if (s === 'pending') { setState('pending'); setMessage('Payment received. Your recharge is being processed.'); }
                else if (s === 'paid_bridge_failed') { setState('pending'); setMessage('Payment received. Delivery had an issue — our team has been notified.'); }
                else { setState('failed'); setMessage('We could not confirm this payment. If money was debited it will auto-refund or reflect shortly.'); setReason(data.pinelabs_status || ''); }
            })
            .catch((e) => { setState('failed'); setMessage(e.response?.data?.detail || 'We could not confirm this payment yet.'); });
    }, [pid]);

    const ICON = {
        loading: <Loader2 className="h-10 w-10 text-[#0E2A47] animate-spin" />,
        completed: <CheckCircle2 className="h-10 w-10 text-emerald-600" />,
        pending: <Clock className="h-10 w-10 text-amber-500" />,
        failed: <XCircle className="h-10 w-10 text-rose-600" />,
    };
    const TITLE = {
        loading: 'Confirming payment',
        completed: 'Recharge successful',
        pending: 'Payment received',
        failed: 'Payment not confirmed',
    };

    return (
        <AppShell>
            <div className="max-w-lg mx-auto py-10">
                <Card className="p-8 text-center" data-testid="payment-return-card">
                    <div className="flex justify-center mb-4">{ICON[state]}</div>
                    <h1 className="font-display text-2xl font-semibold" data-testid="payment-return-title">{TITLE[state]}</h1>
                    <p className="text-sm text-muted-foreground mt-2" data-testid="payment-return-message">{message}</p>
                    {state !== 'loading' && (
                        <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
                            <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="payment-return-dashboard">Go to dashboard</Button>
                            {state === 'failed' && snap
                                ? <Button className="bg-[#0E2A47] hover:bg-[#0B223A]" onClick={retryRazorpay} data-testid="payment-return-retry-razorpay"><RefreshCcw className="h-4 w-4 mr-1.5" /> Retry with Razorpay</Button>
                                : <Button className="bg-[#0E2A47] hover:bg-[#0B223A]" onClick={() => navigate('/recharge')} data-testid="payment-return-recharge">Recharge again</Button>}
                        </div>
                    )}
                </Card>
            </div>
        </AppShell>
    );
}
